import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// Helpers
import { getSections, getSectionsAvailable } from "@PayPlans/helpers/sections";
import { getMappedOEMFranchises } from "@/Common/helpers/OEMs";

// Defaults builders
import {
	BuildVariableOperationsGpsBonusSetup,
	CallActivityPerformanceDefault,
	CashAndLeaseBumpDefault,
	CITBonusDefault,
	CustomerSatisfactionDefault,
	DepartmentOverrideNetIncomeDefault,
	LongevityDefault,
	MaxPayoutDefault,
	PVRQualifierDefault,
	TopPerformanceBonusDefault,
} from "@PayPlans/helpers/DefaultSections";

// API's
import { getSalesPerformanceFormatVersionProducts } from "@Common/api/salesPerformance/queries";

// Contexts/Hooks
import { useLocationFS } from "@/Common/hooks/LocationFS";
import { usePayPlan } from "@PayPlans/context/PayPlans";
import { getExtendedBranchesFranchises, getOEMFranchisePrograms } from "@/Common/hooks/LocationPrograms";

// Components
import PayPlansLayout from "@/PayPlans/Layouts/PayPlansLayout";
import SectionContainer from "@Layouts/components/SectionContainer";

// Custom Sections
import {
	CallActivityPerformance,
	CashAndLeaseBump,
	CitBonus,
	CustomerSatisfaction,
	DefaultCommissions,
	DepartmentOverrideNetIncome,
	Longevity,
	MaxPayout,
	PerformanceNormalization,
	ProductBonuses,
	PVRQualifier,
	TopPerformance,
	VariableOperationsGpsBonus,
} from "./sections";

// MUI
import { Collapse, Grid } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import PdfPayPlanLayer from "@/PayPlans/views/Layers/Html/Sales/Associates";

const allSections = getSections([
	"productBonuses",
	"cashAndLeaseBump",
	"pvrQualifier",
	"departmentOverrideNetIncome",
	"customerSatisfaction",
	"topSalesBonus",
	"callActivityPerformance",
	"maxPayout",
	"cit",
	"longevity",
	"variableOperationsGpsBonus",
]);

// Used to build franchises dependant sections
const excludesMakes = ["Cadillac"];

// Constants
const contentAlignment = "flex-start";

const isSpecialUpdateSection = (section) => ["customerSatisfaction"].includes(section);

const OPTIONAL_SECTIONS = new Map([
	["callActivityPerformance", CallActivityPerformanceDefault],
	["longevity", LongevityDefault],
	["customerSatisfaction", CustomerSatisfactionDefault],
	["topSalesBonus", TopPerformanceBonusDefault],
	["pvrQualifier", PVRQualifierDefault],
	["productBonuses", () => []],
	["cashAndLeaseBump", CashAndLeaseBumpDefault],
	["departmentOverrideNetIncome", DepartmentOverrideNetIncomeDefault],
	["maxPayout", MaxPayoutDefault],
	["cit", CITBonusDefault],
	["variableOperationsGpsBonus", BuildVariableOperationsGpsBonusSetup],
]);

const getIsSectionButtonDisabled = ({ isValid = false, isReadOnlyReason = false, isSomeNewPayPlan = false, loading = false }) =>
	!isValid || isReadOnlyReason || isSomeNewPayPlan || loading;

export default function PayPlansModel(props) {
	const { data = null, location = null, isNew = false, loading = false, readOnly = false, updateModel = () => {} } = props;

	// Hooks
	const { isSomeNewPayPlan, isValid, selected, useSectionsErrors, position } = usePayPlan();
	const { errors, clearErrors, clearSectionErrors } = useSectionsErrors();
	const { data: locationFS, isLoading: loadingFS } = useLocationFS({ locationId: location.locationId });

	const isReadOnly = !loading && (Boolean(data?.available) || position !== data?.position);
	const removeSections = data?.position === "F&I Manager" ? ["departmentOverrideNetIncome"] : [];

	// States
	const [isLazyLoading, setIsLazyLoading] = useState(true);
	const [sections, setSections] = useState(new Map());

	const { data: formatVersion, isLoading: loadingProducts } = useQuery({
		queryKey: ["salesPerformanceProducts", location?.locationId, data?.formatVersion],
		queryFn: async () => getSalesPerformanceFormatVersionProducts(location?.locationId, data?.formatVersion),
		staleTime: Infinity,
		enabled: !!location?.locationId && !!data?.formatVersion,
	});

	// Programs usage
	const programsUsage = useMemo(() => {
		const extendedBranchesFranchises = getExtendedBranchesFranchises(location?.branchesOEMs, location?.branchesFranchises);
		return getOEMFranchisePrograms(locationFS?.oems, extendedBranchesFranchises);
	}, [location, locationFS]);

	// Sections controls
	const handleAddSection = (key) => {
		const sectionFn = OPTIONAL_SECTIONS.get(key);
		const isRequiredSectionParams = ["customerSatisfaction", "variableOperationsGpsBonus"].includes(key);

		if (isRequiredSectionParams) {
			switch (key) {
				case "variableOperationsGpsBonus":
					data[key] = sectionFn({ franchises: location?.franchises });
					break;
				case "customerSatisfaction":
					const mappedOEMFranchises = getMappedOEMFranchises(location?.branchesOEMs ?? [], location?.branchesFranchises ?? []);
					data[key] = sectionFn(mappedOEMFranchises ?? [], excludesMakes, { useOnlyStoreMetrics: true });
					break;
			}
		} else {
			data[key] = sectionFn();
		}

		setSections(getSectionsAvailable(allSections, data, removeSections));
	};

	const handleDeleteSection = (section) => {
		const remove = allSections.get(section);

		// Remove section
		data[section] = null;
		if (remove?.type === "list") {
			data[section] = [];
		}

		clearSectionErrors(section);
		setSections(getSectionsAvailable(allSections, data, removeSections));
		updateModel({ section, data: data[section] });
	};

	const handleUpdateSection = (section, update = "", value = null, isValidUpdate = true) => {
		if (isSpecialUpdateSection(section)) {
			if (update === "weighting") {
				data[section][update] = value;
			} else {
				data[section]["makeMetrics"]?.forEach((makeMetric, key) => {
					if (makeMetric.make === update) {
						data[section]["makeMetrics"][key] = value;
					}
				});
			}
		} else {
			data[section] = value;
		}

		// Update model
		Boolean(isValidUpdate) && updateModel({ section, data: data[section] });
	};

	// Sections to disable
	const disableSections = programsUsage?.filter((item) => item.disabled).map((item) => item.program) || [];

	useMemo(() => {
		setSections(getSectionsAvailable(allSections, data, removeSections));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const isDOMLoading = useMemo(() => isLazyLoading || loading || loadingFS, [isLazyLoading, loading, loadingFS]);

	useMemo(() => {
		const timer = 500;
		setIsLazyLoading(true);
		if (selected) {
			setTimeout(() => {
				setIsLazyLoading(false);
			}, timer);
		}
	}, [selected, setIsLazyLoading]);

	useEffect(() => {
		return () => {
			clearErrors();
		};
	}, [clearErrors]);

	const isReadOnlyReason = readOnly || isReadOnly;

	return (
		<PayPlansLayout
			htmlComponent={<PdfPayPlanLayer payPlan={data} selected={selected} loading={loading} />}
			data={data}
			allSections={allSections}
			sections={sections}
			disabledSections={disableSections}
			disabled={getIsSectionButtonDisabled({
				isValid,
				isReadOnlyReason,
				isSomeNewPayPlan,
				loading,
			})}
			isNew={isNew}
			isValid={isValid}
			loading={loading}
			readOnly={isReadOnlyReason}
			onActionAddSection={handleAddSection}
		>
			<TransitionGroup component={Grid} container item xs={12}>
				<Collapse sx={{ width: 1 }}>
					<SectionContainer
						sectionId={"default"}
						title={"Default Commissions"}
						collapsible
						loading={loading}
						readOnly={isReadOnlyReason}
					>
						<DefaultCommissions
							sectionId={"default"}
							commissions={{
								new: data?.default?.new || null,
								preOwned: data?.default?.preOwned || null,
							}}
							globalGrossAndUnits={data?.default?.globalGrossAndUnits}
							isGuaranteedDraw={data?.default?.isGuaranteedDraw}
							useSectionsErrors={useSectionsErrors}
							errors={errors?.get("default")}
							formatVersion={formatVersion}
							loading={loading}
							loadingProducts={loadingProducts}
							readOnly={isReadOnlyReason}
							onActionUpdate={handleUpdateSection}
						/>
					</SectionContainer>
				</Collapse>

				{sections.get("productBonuses") && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"productBonuses"}
							title={sections.get("productBonuses").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<ProductBonuses
								sectionId={"productBonuses"}
								products={data?.productBonuses || []}
								useSectionsErrors={useSectionsErrors}
								formatVersion={formatVersion}
								loading={loading}
								loadingProducts={loadingProducts}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{sections.get("cashAndLeaseBump") && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"cashAndLeaseBump"}
							title={sections.get("cashAndLeaseBump").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CashAndLeaseBump
								sectionId={"cashAndLeaseBump"}
								content={data?.cashAndLeaseBump || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{sections.get("callActivityPerformance") && !isDOMLoading && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"callActivityPerformance"}
							title={sections.get("callActivityPerformance").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CallActivityPerformance
								sectionId={"callActivityPerformance"}
								performance={data?.callActivityPerformance || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{sections.get("cit") && !isDOMLoading && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"cit"}
							title={sections.get("cit").title}
							collapsible
							deletable
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<CitBonus
								sectionId={"cit"}
								content={data?.cit}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}

				{sections.get("variableOperationsGpsBonus") && !isDOMLoading && (
					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"variableOperationsGpsBonus"}
							title={sections.get("variableOperationsGpsBonus").title}
							collapsible
							deletable
							loading={loading}
							readOnly={readOnly || isReadOnly}
							onDeleteAction={handleDeleteSection}
						>
							<VariableOperationsGpsBonus
								sectionId={"variableOperationsGpsBonus"}
								position={position}
								content={data?.variableOperationsGpsBonus || null}
								useSectionsErrors={useSectionsErrors}
								loading={loading}
								readOnly={readOnly || isReadOnly}
								franchises={location?.franchises}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>
				)}				
			</TransitionGroup>

			<Grid container item xs={12} columnSpacing={2} pt={"0px !important"}>
				<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
					{!removeSections.includes("departmentOverrideNetIncome") &&
						sections.get("departmentOverrideNetIncome") &&
						!isDOMLoading && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"departmentOverrideNetIncome"}
									title={sections.get("departmentOverrideNetIncome").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<DepartmentOverrideNetIncome
										sectionId={"departmentOverrideNetIncome"}
										content={data?.departmentOverrideNetIncome || null}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

					<Collapse sx={{ width: 1, marginTop: 2 }}>
						<SectionContainer
							sectionId={"backendPVRComputation"}
							title={"Performance Normalization"}
							collapsible
							loading={loading}
							readOnly={isReadOnlyReason}
							onDeleteAction={handleDeleteSection}
						>
							<PerformanceNormalization
								sectionId={"backendPVRComputation"}
								normalization={data?.backendPVRComputation}
								loading={loading}
								readOnly={isReadOnlyReason}
								onActionUpdate={handleUpdateSection}
							/>
						</SectionContainer>
					</Collapse>

					{sections.get("maxPayout") && (
						<Collapse sx={{ width: 1, marginTop: 2 }}>
							<SectionContainer
								sectionId={"maxPayout"}
								title={sections.get("maxPayout").title}
								collapsible
								deletable
								loading={loading}
								readOnly={isReadOnlyReason}
								onDeleteAction={handleDeleteSection}
							>
								<MaxPayout
									sectionId={"maxPayout"}
									content={data?.maxPayout}
									useSectionsErrors={useSectionsErrors}
									loading={loading}
									readOnly={isReadOnlyReason}
									onActionUpdate={handleUpdateSection}
								/>
							</SectionContainer>
						</Collapse>
					)}
				</TransitionGroup>

				{!isDOMLoading && (
					<TransitionGroup component={Grid} container item xs={6} alignContent={contentAlignment} alignItems={contentAlignment}>
						{sections.get("pvrQualifier") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"pvrQualifier"}
									title={sections.get("pvrQualifier").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<PVRQualifier
										sectionId={"pvrQualifier"}
										qualifiers={data?.pvrQualifier || []}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("topSalesBonus") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"topSalesBonus"}
									title={sections.get("topSalesBonus").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<TopPerformance
										content={data?.topSalesBonus}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("customerSatisfaction") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"customerSatisfaction"}
									title={sections.get("customerSatisfaction").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<CustomerSatisfaction
										sectionId={"customerSatisfaction"}
										content={data?.customerSatisfaction}
										errors={errors?.get("customerSatisfaction")}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}

						{sections.get("longevity") && (
							<Collapse sx={{ width: 1, marginTop: 2 }}>
								<SectionContainer
									sectionId={"longevity"}
									title={sections.get("longevity").title}
									collapsible
									deletable
									loading={loading}
									readOnly={isReadOnlyReason}
									onDeleteAction={handleDeleteSection}
								>
									<Longevity
										content={data?.longevity}
										useSectionsErrors={useSectionsErrors}
										loading={loading}
										readOnly={isReadOnlyReason}
										onActionUpdate={handleUpdateSection}
									/>
								</SectionContainer>
							</Collapse>
						)}
					</TransitionGroup>
				)}
			</Grid>
		</PayPlansLayout>
	);
}
PayPlansModel.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
	isNew: PropTypes.bool,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	updateModel: PropTypes.func,
};
